import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { artist, totals, services, testimonials, marqueeExtras, trustedBy } from "../mock";
import { useClients } from "../contexts/ClientsContext";
import ClientCard from "../components/ClientCard";

const HomePage = () => {
  const { clients } = useClients();
  const featured = clients.filter((c) => c.featured);
  const others = clients.filter((c) => !c.featured);

  return (
    <div>
      {/* HERO */}
      <section className="relative max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-28 pb-16 md:pb-24">
        <div className="grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8 fade-up">
            <p className="font-mono-label text-[#F62493] mb-6">Portfolio — {totals.years} Years, {totals.clients} Clients, {totals.projects}+ Works</p>
            <h1 className="font-display text-[2.8rem] sm:text-6xl md:text-7xl lg:text-[6.5rem] leading-[0.98] tracking-[-0.02em] text-stone-100">
              Crafting <em className="italic font-normal text-[#F62493]">experiences</em> through impactful design.
            </h1>
          </div>
          <div className="md:col-span-4 fade-up delay-2">
            <div className="mb-8 relative w-40 h-40 md:w-48 md:h-48 overflow-hidden border border-stone-800">
              <img
                src={artist.photo}
                alt={artist.name}
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
            <p className="text-lg md:text-xl text-stone-200 leading-relaxed">
              I&apos;m {artist.name.split(" ")[0]} — a Senior Graphic Designer with two decades of experience in marketing, branding, and creative storytelling. Below: my work, organized by the clients it was made for.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/clients"
                className="inline-flex items-center gap-2 rounded-full bg-[#171717] text-[#F7F3ED] px-6 py-3 hover:bg-[#F62493] transition-colors"
              >
                View all clients <ArrowRight size={16} />
              </Link>
              <Link
                to="/contact#contact-form"
                className="inline-flex items-center gap-2 rounded-full border border-stone-900 text-stone-100 px-6 py-3 hover:bg-[#171717] hover:text-[#F7F3ED] transition-colors"
              >
                Start a project
              </Link>
            </div>
          </div>
        </div>

        {/* Hero collage strip */}
        <div className="mt-16 md:mt-24 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5 fade-up delay-3">
          {[
            "https://cdn.crevado.com/artists/1062266/artwork/19394394_mobile.jpg",
            "https://cdn.crevado.com/artists/1062266/artwork/19360454_mobile-1.jpg",
            "https://cdn.crevado.com/artists/1062266/artwork/19360462_mobile-1.jpg",
            "https://cdn.crevado.com/artists/1062266/artwork/19360514_mobile-1.jpg"
          ].map((src, i) => (
            <div key={i} className="aspect-[4/5] overflow-hidden">
              <img src={src} alt="" className="w-full h-full object-cover img-fade hover:scale-105" loading="lazy" />
            </div>
          ))}
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-stone-800 bg-[#0A0A0A] overflow-hidden py-6">
        <div className="marquee-track flex items-center gap-16 whitespace-nowrap">
          {(() => {
            const names = [...clients.map((c) => c.name), ...marqueeExtras];
            return [...names, ...names].map((n, i) => (
              <span key={i} className="font-display text-3xl md:text-5xl italic text-stone-200">
                {n} <span className="text-[#F62493] mx-6">✺</span>
              </span>
            ));
          })()}
        </div>
      </section>

      {/* FEATURED CLIENTS */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-32">
        <div className="flex items-end justify-between mb-12 md:mb-16">
          <div>
            <p className="font-mono-label text-stone-300 mb-3">Selected Clients</p>
            <h2 className="font-display text-4xl md:text-6xl leading-tight text-stone-100">
              Featured work, <em className="italic text-[#F62493]">by client</em>.
            </h2>
          </div>
          <Link to="/clients" className="hidden md:inline-flex items-center gap-2 font-mono-label text-stone-200 link-underline">
            View all <ArrowUpRight size={16} />
          </Link>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          {featured.map((c, i) => (
            <ClientCard key={c.slug} client={c} index={i} />
          ))}
        </div>
      </section>

      {/* ABOUT SNIPPET */}
      <section className="bg-[#171717] text-[#F7F3ED] py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <p className="font-mono-label text-stone-300 mb-5">About</p>
            <h2 className="font-display text-4xl md:text-6xl leading-[1.05]">
              Twenty years of high-stakes creative — delivered with a sense of humor.
            </h2>
            <p className="mt-8 text-lg text-stone-200 leading-relaxed max-w-2xl">
              From Fortune 500 rebrands to boutique small-business identities, I partner with marketing teams, agencies, and founders to turn strategy into design that actually performs.
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 mt-10 rounded-full border border-stone-500 hover:border-[#F7F3ED] px-6 py-3 transition-colors"
            >
              Full story <ArrowRight size={16} />
            </Link>
          </div>
          <div className="md:col-span-5 grid grid-cols-2 gap-5">
            {[
              { n: totals.years + "+", l: "Years designing" },
              { n: totals.clients, l: "Active client brands" },
              { n: totals.projects + "+", l: "Projects shipped" },
              { n: totals.countries, l: "Countries of reach" }
            ].map((s) => (
              <div key={s.l} className="border border-stone-400 rounded-sm p-6">
                <div className="font-display text-4xl md:text-5xl text-[#F7F3ED]">{s.n}</div>
                <div className="font-mono-label text-stone-300 mt-3">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <p className="font-mono-label text-stone-300 mb-3">Capabilities</p>
        <h2 className="font-display text-4xl md:text-6xl text-stone-100 leading-tight max-w-4xl">
          A full creative toolkit, from identity to the printer bed.
        </h2>

        <ul className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10">
          {services.map((s, i) => (
            <li key={s} className="border-t border-stone-800 py-6 flex items-start gap-4 group cursor-default">
              <span className="font-mono-label text-stone-300 pt-1">{String(i + 1).padStart(2, "0")}</span>
              <span className="font-display text-xl md:text-2xl text-stone-100 group-hover:text-[#F62493] transition-colors">
                {s}
              </span>
            </li>
          ))}
        </ul>
      </section>

      {/* MORE CLIENTS */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24 md:pb-32">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="font-mono-label text-stone-300 mb-3">Also Worked With</p>
            <h2 className="font-display text-3xl md:text-5xl text-stone-100">More collaborations.</h2>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {others.map((c, i) => (
            <ClientCard key={c.slug} client={c} index={featured.length + i} />
          ))}
        </div>
      </section>

      {/* TRUSTED BY — Fortune-class brands */}
      <section className="border-t border-stone-800 bg-[#0A0A0A] py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-8 items-end mb-10 md:mb-14">
            <div className="md:col-span-7">
              <p className="font-mono-label text-stone-300 mb-3">Trusted By</p>
              <h2 className="font-display text-3xl md:text-5xl text-stone-100 leading-tight">
                Brand experience across <em className="italic text-[#F62493]">Fortune 500</em> rooms.
              </h2>
            </div>
            <p className="md:col-span-5 text-stone-200 leading-relaxed">
              Through agency and in-house engagements, my work has supported creative for some of the most recognizable brands in the world.
            </p>
          </div>

          <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-3 gap-x-10 gap-y-6 border-t border-stone-800 pt-10">
            {trustedBy.map((brand, i) => (
              <li
                key={brand}
                className="flex items-baseline gap-4 border-b border-stone-800 pb-4 hover:border-[#F62493] transition-colors"
              >
                <span className="font-mono-label text-stone-300 text-[10px] pt-1">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-display text-xl md:text-2xl text-stone-100">{brand}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* TESTIMONIALS — color block */}
      <section className="bg-[#F62493] py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between mb-12 md:mb-16">
            <div>
              <p className="font-mono-label text-white/80 mb-3">Kind Words</p>
              <h2 className="font-display text-3xl md:text-5xl text-white leading-tight max-w-2xl">
                What clients &amp; colleagues say.
              </h2>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-14">
            {testimonials.map((t, i) => (
              <blockquote key={i} className="border-t border-white/40 pt-6 flex flex-col">
                <p className="font-display text-lg md:text-xl text-white leading-snug flex-1">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <footer className="mt-6">
                  <p className="font-display text-base text-white">{t.author}</p>
                  <p className="font-mono-label text-white/80 mt-1">{t.company}</p>
                </footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
